import type { DateTimeFilterBounds } from '@/shared/lib/dateTimeFilter';
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { PROBLEM_EXECUTOR_COLUMNS } from '@/entities/ProblemExecutor/config/problem-executor.config';
import { toProblemExecutorSearchRequest } from '@/entities/ProblemExecutor/mappers';
import { useProblemExecutors } from '@/entities/ProblemExecutor/model/store';
import type { ProblemExecutorSearchFilters } from '@/entities/ProblemExecutor/model/types';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { cleanPhone } from '@/shared/lib/phone';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { InputText } from '@/shared/ui/InputText/InputText';
import { PersonSearchFilter } from '@/shared/ui/PersonSearchFilter/PersonSearchFilter';
import { RequestEmpty, RequestErrorBanner } from '@/shared/ui/RequestState';
import classes from './ProblemExecutorsPage.module.scss';

/** Показывает клиентские отзывы с оценками от одной до трёх звёзд */
const ProblemExecutorsPage = () => {
    const canView = useCanAccessPermission('admin.problem-executors.view');
    const items = useProblemExecutors(state => state.items);
    const allCount = useProblemExecutors(state => state.allCount);
    const status = useProblemExecutors(state => state.status);
    const error = useProblemExecutors(state => state.error);
    const load = useProblemExecutors(state => state.load);
    const reset = useProblemExecutors(state => state.reset);
    const [mark, setMark] = useState<number | undefined>();
    const [dateRange, setDateRange] = useState<DateTimeFilterBounds>({});
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(25);

    const [search, setSearch] = useState<ProblemExecutorSearchFilters>({
        executorFioSearch: '',
        executorPhoneSearch: '',
        clientNameSearch: '',
        clientPhoneSearch: '',
        petNameSearch: '',
    });

    const debouncedSearch = useDebouncedValue(search, 300);
    const searchRequest = useMemo(() => toProblemExecutorSearchRequest(debouncedSearch), [debouncedSearch]);

    const executorPhoneError = search.executorPhoneSearch && !cleanPhone(search.executorPhoneSearch)
        ? 'Введите полный номер телефона'
        : undefined;

    const clientPhoneError = search.clientPhoneSearch && !cleanPhone(search.clientPhoneSearch)
        ? 'Введите полный номер телефона'
        : undefined;

    const canSearch = !(debouncedSearch.executorPhoneSearch && !searchRequest.executorPhoneSearch)
        && !(debouncedSearch.clientPhoneSearch && !searchRequest.clientPhoneSearch);

    const refresh = useCallback(async () => {
        if (!canSearch) return;

        await load({
            createdFrom: dateRange.from,
            createdTo: dateRange.to,
            ...searchRequest,
            mark,
            skipCount: first,
            takeCount: rows,
        });
    }, [canSearch, load, searchRequest, mark, first, rows, dateRange]);

    useEffect(() => {
        if (canView) void refresh();
    }, [canView, refresh]);

    useEffect(() => reset, [reset]);

    if (!canView) return <RequestEmpty message="Нет доступа к проблемным исполнителям" />;

    return <div className={classes.page}>
        <div className={classes.heading}>
            <div>
                <h1>Проблемные исполнители</h1>
                <p>Отзывы клиентов с оценками 1–3 звезды. Каждый отзыв показан отдельно, новые — сверху.</p>
            </div>
            <ActionButton
                label="Обновить"
                icon="pi pi-refresh"
                loading={status === RequestStatus.Loading}
                disabled={Boolean(executorPhoneError || clientPhoneError)}
                onClick={() => void refresh()}
            />
        </div>
        <div className={classes.searchFilters}>
            <PersonSearchFilter
                name="problem-executor-search"
                label="Исполнитель"
                namePlaceholder="Имя или фамилия исполнителя"
                nameValue={search.executorFioSearch}
                phoneValue={search.executorPhoneSearch}
                error={executorPhoneError}
                onChange={(mode, value) => {
                    setFirst(0);

                    setSearch(current => ({
                        ...current,
                        executorFioSearch: mode === 'name'
                            ? value
                            : '',
                        executorPhoneSearch: mode === 'phone'
                            ? value
                            : '',
                    }));
                }}
            />
            <PersonSearchFilter
                name="problem-client-search"
                label="Клиент"
                namePlaceholder="Имя клиента"
                nameValue={search.clientNameSearch}
                phoneValue={search.clientPhoneSearch}
                error={clientPhoneError}
                onChange={(mode, value) => {
                    setFirst(0);

                    setSearch(current => ({
                        ...current,
                        clientNameSearch: mode === 'name'
                            ? value
                            : '',
                        clientPhoneSearch: mode === 'phone'
                            ? value
                            : '',
                    }));
                }}
            />
            <InputText
                name="problem-pet-search"
                label="Кличка питомца"
                placeholder="Кличка или её часть"
                value={search.petNameSearch}
                onChange={event => {
                    setFirst(0);

                    setSearch(current => ({
                        ...current,
                        petNameSearch: event.target.value,
                    }));
                }}
            />
        </div>
        <div
            className={classes.filters}
            role="group"
            aria-label="Оценка клиента"
        >
            <ActionButton
                label="Все оценки 1–3"
                outlined={mark !== undefined}
                aria-pressed={mark === undefined}
                onClick={() => {
                    setMark(undefined);
                    setFirst(0);
                }}
            />
            {[1, 2, 3].map(value => <ActionButton
                key={value}
                label={`${value} ★`}
                aria-label={`Оценка ${value}`}
                outlined={mark !== value}
                aria-pressed={mark === value}
                onClick={() => {
                    setMark(value);
                    setFirst(0);
                }}
            />)}
        </div>
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить проблемных исполнителей"
            onRetry={refresh}
        />
        <DataTable
            onDateFilterChange={(_field, bounds) => {
                setDateRange(bounds ?? {});
                setFirst(0);
            }}
            title="Отзывы с низкими оценками"
            data={items}
            columns={PROBLEM_EXECUTOR_COLUMNS}
            filterDisplay="menu"
            loading={status === RequestStatus.Loading}
            paginator
            lazy
            first={first}
            rows={rows}
            rowsPerPageOptions={[10, 25, 50, 100]}
            totalRecords={allCount}
            onPageChange={event => {
                setFirst(event.first);
                setRows(event.rows);
            }}
            emptyMessage={status === RequestStatus.Error
                ? 'Не удалось загрузить отзывы.'
                : 'Отзывов с такой оценкой пока нет.'
            }
        />
    </div>;
};

export default ProblemExecutorsPage;
